import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../context/AuthContext";
import { ShieldCheck, Envelope, Lock, Eye, EyeSlash } from "@phosphor-icons/react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_admin-dashboard-flux/artifacts/ef1ryefo_The%20Gowda%E2%80%99s%20Group.png";
const BG_URL = "https://static.prod-images.emergentagent.com/jobs/d7f48274-665e-4130-b577-6d1e990ded15/images/269834a82af8a33b2b6d1aa6341da49cc1cf70d7f6d6225aa46801ec3fb58b8a.png";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Enter both email and password");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: email.trim(), password });
      login(res.data.user, res.data.session_token);
      toast.success(`Welcome back, ${res.data.user.name || "Admin"}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error("Enter your Gmail ID"); return; }
    setForgotSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      toast.success("If that email is registered, a reset link has been sent.");
      setShowForgot(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed");
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative"
      style={{ background: `url(${BG_URL}) center/cover no-repeat, #0A0A0A` }}
      data-testid="login-page"
    >
      <div className="absolute inset-0 bg-[#0A0A0A]/70" />
      <div
        className="relative z-10 w-full max-w-md mx-4 p-8 rounded-lg border border-white/10"
        style={{ background: "#121212" }}
      >
        <div className="flex flex-col items-center mb-6">
          <img src={LOGO_URL} alt="VeloxAlgoTech LLP" className="w-16 h-16 mb-3" />
          <h1
            className="text-2xl font-black tracking-tight text-center"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif", letterSpacing: "0.06em" }}
            data-testid="brand-name-admin-login"
          >
            <span style={{ color: '#00b4d8' }}>Velox</span>
            <span className="text-white">AlgoTech</span>
          </h1>
          <p className="text-xs text-[#A1A1AA] mt-1">Master Admin Axis</p>
        </div>

        <div className="flex items-center gap-2 mb-5 p-2.5 rounded-md border border-white/10 bg-white/5">
          <ShieldCheck size={16} weight="duotone" className="text-[#00E5FF]" />
          <span className="text-[10px] text-[#A1A1AA]">
            Master &amp; Slave admins can sign in below. Only authorized accounts have access.
          </span>
        </div>

        {/* Email + Password form */}
        <form onSubmit={handlePasswordLogin} className="space-y-3 mb-4" data-testid="admin-login-form">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA] mb-1 block">Gmail ID</label>
            <div className="relative">
              <Envelope size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="admin-login-email"
                className="w-full pl-9 pr-3 py-2.5 rounded-md bg-[#0F0F0F] border border-white/10 text-sm text-white placeholder-[#52525B] focus:outline-none focus:border-[#00E5FF]/40"
                placeholder="admin@example.com"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA] mb-1 block">Password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="admin-login-password"
                className="w-full pl-9 pr-10 py-2.5 rounded-md bg-[#0F0F0F] border border-white/10 text-sm text-white placeholder-[#52525B] focus:outline-none focus:border-[#00E5FF]/40"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#A1A1AA] transition-colors"
              >
                {showPassword ? <EyeSlash size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full py-2.5 rounded-md text-sm font-bold text-black transition-all disabled:opacity-50"
            style={{ background: "#00E5FF" }}
          >
            {submitting ? "Signing in..." : "Sign in"}
          </button>
          <button
            type="button"
            onClick={() => setShowForgot(true)}
            data-testid="admin-forgot-password-link"
            className="w-full text-[11px] text-[#A1A1AA] hover:text-white transition-colors mt-1"
          >
            Forgot password?
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] text-[#52525B] uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <button
          onClick={handleGoogleLogin}
          data-testid="google-login-button"
          className="w-full py-2.5 px-4 rounded-md font-bold text-sm text-white border border-white/10 bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center gap-3"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
          </svg>
          Sign in with Google
        </button>

        <p className="text-center text-[10px] text-[#52525B] mt-5">
          First-time Google sign-in creates Master Admin (or Slave if one already exists).
        </p>
      </div>

      {/* Forgot password modal */}
      {showForgot && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="forgot-password-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowForgot(false); }}
        >
          <div className="w-full max-w-sm p-6 rounded-xl border border-white/10 bg-zinc-950">
            <h3 className="text-sm font-bold text-white mb-1">Reset your password</h3>
            <p className="text-[11px] text-[#A1A1AA] mb-4">We'll send a reset link to your Gmail ID if it's registered as an admin.</p>
            <form onSubmit={handleForgot} className="space-y-3">
              <div className="relative">
                <Envelope size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
                <input
                  type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com" required
                  data-testid="forgot-email-input"
                  className="w-full pl-9 pr-3 py-2.5 rounded-md bg-[#0F0F0F] border border-white/10 text-sm text-white placeholder-[#52525B] focus:outline-none focus:border-[#00E5FF]/40"
                />
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setShowForgot(false)}
                  className="flex-1 py-2 rounded-md text-xs text-[#A1A1AA] hover:text-white border border-white/10 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={forgotSubmitting} data-testid="forgot-send-btn"
                  className="flex-1 py-2 rounded-md text-xs font-bold text-black bg-[#00E5FF] hover:bg-[#00B8CC] transition-colors disabled:opacity-50">
                  {forgotSubmitting ? "Sending..." : "Send reset link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
